/**
 * YouTube Helper Functions
 * Utilities for extracting video IDs and generating thumbnail URLs
 */

/**
 * Extract YouTube video ID from various URL formats
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 */
export const getYouTubeVideoId = (url) => {
  if (!url) return null;

  // Remove whitespace
  url = url.trim();

  // Pattern 1: youtube.com/watch?v=VIDEO_ID
  let match = url.match(/[?&]v=([^&#]+)/);
  if (match) return match[1];

  // Pattern 2: youtu.be/VIDEO_ID
  match = url.match(/youtu\.be\/([^?&#]+)/);
  if (match) return match[1];

  // Pattern 3: youtube.com/embed/VIDEO_ID
  match = url.match(/youtube\.com\/embed\/([^?&#]+)/);
  if (match) return match[1];

  // Pattern 4: youtube.com/v/VIDEO_ID
  match = url.match(/youtube\.com\/v\/([^?&#]+)/);
  if (match) return match[1];

  return null;
};

/**
 * Get YouTube thumbnail URL from video URL
 * @param {string} url - YouTube video URL
 * @param {string} quality - Thumbnail quality: 'default', 'medium', 'high', 'standard', 'maxres'
 * @returns {string|null} - Thumbnail URL or null if invalid
 */
export const getYouTubeThumbnail = (url, quality = 'high') => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;

  // Quality options:
  // default: 120x90
  // medium: 320x180 (mqdefault)
  // high: 480x360 (hqdefault)
  // standard: 640x480 (sddefault)
  // maxres: 1280x720 (maxresdefault)

  const qualityMap = {
    default: 'default',
    medium: 'mqdefault',
    high: 'hqdefault',
    standard: 'sddefault',
    maxres: 'maxresdefault'
  };

  const thumbnailQuality = qualityMap[quality] || 'hqdefault';
  return `https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`;
};

/**
 * Get YouTube embed URL from video URL
 * @param {string} url - YouTube video URL
 * @returns {string|null} - Embed URL or null if invalid
 */
export const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;

  return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Validate if a URL is a valid YouTube URL
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid YouTube URL
 */
export const isValidYouTubeUrl = (url) => {
  if (!url) return false;
  return getYouTubeVideoId(url) !== null;
};
